import { useContext, useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import GroupPostCommentForm from "@serie3/mymediaplanner/Modules/Groups/Forms/GroupPostCommentForm";
import PostPreviewPanel from "../Previews/PostPreviewPanel";
import SettingsContext from "@serie3/common/auth/SettingsContext";

const formatDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d)) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) +
    " · " +
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const Avatar = ({ name }) => {
  const initials = name
    ? name.trim().split(/\s+/).slice(0, 2).map((w) => w[0].toUpperCase()).join("")
    : "?";
  return (
    <div style={{
      width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
      background: "linear-gradient(135deg, #564ab1, #3d3490)",
      color: "#fff", fontSize: 12, fontWeight: 700,
      display: "flex", alignItems: "center", justifyContent: "center",
      letterSpacing: 0.5,
    }}>
      {initials}
    </div>
  );
};

const GroupPostViewModal = ({ show, setShow, share, group }) => {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);
  const currentUser = settings?.user?.username;
  const [comments, setComments] = useState([]);
  const [editingId, setEditingId] = useState(0);
  const [loadingComments, setLoadingComments] = useState(false);

  useEffect(() => {
    if (!show || !share?.id) return;
    setComments([]);
    setLoadingComments(true);
    crudApi
      .justGet(MediaplannerGroupEndpoints.endpoints.listCommentsOnPost + group.id + "/" + share.id)
      .then((r) => { setComments(r.ok && Array.isArray(r.data?.comments) ? r.data.comments : []); })
      .finally(() => setLoadingComments(false));
  }, [show, share?.id]);

  const commentForm = GroupPostCommentForm.createGroupPostCommentForm(
    comments,
    setComments,
    group?.id,
    share?.id,
    setEditingId,
  );

  const deleteComment = async (commentId) => {
    const response = await crudApi.del(
      MediaplannerGroupEndpoints.endpoints.delete + group.id + "/",
      commentId,
    );
    if (response.status === 200) {
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    }
  };

  const formik = useFormik(commentForm);

  // Attached images, via the member-gated group route (the viewer is usually not the owner).
  const [files, setFiles] = useState([]);
  useEffect(() => {
    if (!show || !share?.id || !group?.id) return;
    let cancelled = false;
    setFiles([]);
    crudApi
      .getAll(MediaplannerGroupEndpoints.endpoints.getAllImagesForPost + group.id + "/" + share.id)
      .then((r) => { if (!cancelled && r?.ok) setFiles(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [show, share?.id, group?.id]);

  if (!share) return null;
  const post = share.post;

  return (
    <Modal show={show} onHide={() => setShow(false)} size="xl" centered>
      <Modal.Header closeButton style={{
        borderBottom: "1px solid #eaecf0",
        padding: "16px 24px",
      }}>
        <Modal.Title style={{ fontSize: 15, fontWeight: 700, color: "#1a1f2e" }}>
          {post?.title || t("Backend.Groups.Post.ViewTitle")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: 0 }}>
        {/* Content on the left, facts on the right */}
        <div style={{ borderBottom: "1px solid #eaecf0" }}>
          <PostPreviewPanel
            post={post}
            author={share?.userinfo}
            files={files}
            binaryEndpoint={MediaplannerGroupEndpoints.endpoints.getImageBinary + group?.id + "/"}
          />
        </div>

        {/* Comments section */}
        <div style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <span style={{
              fontSize: 12, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: 0.8, color: "#564ab1",
            }}>
              {t("Backend.Groups.Post.Comments")}
            </span>
            {comments.length > 0 && (
              <span style={{
                background: "rgba(86,74,177,0.1)", color: "#564ab1",
                fontSize: 11, fontWeight: 700, borderRadius: 20,
                padding: "1px 8px",
              }}>
                {comments.length}
              </span>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
            {loadingComments && (
              <>
                {[1, 2].map((i) => (
                  <div key={i} style={{ display: "flex", gap: 10 }}>
                    <div className="skeleton" style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0 }} />
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <div className="skeleton" style={{ height: 12, width: "25%", borderRadius: 6 }} />
                        <div className="skeleton" style={{ height: 10, width: "18%", borderRadius: 6, opacity: 0.6 }} />
                      </div>
                      <div className="skeleton" style={{ height: 12, width: "80%", borderRadius: 6 }} />
                      <div className="skeleton" style={{ height: 12, width: "55%", borderRadius: 6 }} />
                    </div>
                  </div>
                ))}
              </>
            )}
            {!loadingComments && comments.length === 0 && (
              <div style={{
                textAlign: "center", padding: "24px 0",
                color: "#9299a8", fontSize: 13,
              }}>
                {t("Backend.Groups.Post.NoComments")}
              </div>
            )}
            {!loadingComments && comments.map((c) => {
              const dateStr = formatDate(c.last_edit ?? null);
              return (
                <div key={c.id} style={{ display: "flex", gap: 10 }}>
                  <Avatar name={c.user?.username} />
                  <div style={{
                    flex: 1,
                    background: "#f8f9fc",
                    borderRadius: "4px 12px 12px 12px",
                    padding: "10px 14px",
                    border: "1px solid #eaecf0",
                  }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#1a1f2e" }}>
                        {c.user?.username ?? ""}
                      </span>
                      {dateStr && (
                        <span style={{ fontSize: 11, color: "#9299a8" }}>
                          {dateStr}
                        </span>
                      )}
                      {c.user?.username === currentUser && (
                        <button
                          type="button"
                          onClick={() => deleteComment(c.id)}
                          title={t("Common.Delete")}
                          style={{
                            marginLeft: "auto", background: "none", border: "none",
                            padding: "2px 4px", cursor: "pointer", color: "#cdd0d6",
                            fontSize: 11, lineHeight: 1,
                            transition: "color 0.15s ease",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = "#e74c3c"}
                          onMouseLeave={(e) => e.currentTarget.style.color = "#cdd0d6"}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                    <p style={{ fontSize: 13, color: "#374151", margin: 0, lineHeight: 1.5 }}>
                      {c.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* New comment input */}
          <Form onSubmit={formik.handleSubmit}>
            <div style={{
              display: "flex", gap: 10, alignItems: "flex-end",
              background: "#f8f9fc", borderRadius: 12,
              border: "1px solid #e0e3ea", padding: "10px 12px",
            }}>
              <Avatar name={share?.userinfo?.username} />
              <Form.Control
                as="textarea"
                rows={2}
                name="text"
                value={formik.values.text}
                onChange={formik.handleChange}
                placeholder={t("Backend.Groups.Post.CommentPlaceholder")}
                style={{
                  flex: 1, border: "none", background: "transparent",
                  resize: "none", fontSize: 13, padding: 0, boxShadow: "none",
                  outline: "none",
                }}
              />
              <Button
                type="submit"
                disabled={formik.isSubmitting || !formik.values.text}
                style={{
                  padding: "7px 18px", borderRadius: 20, border: "none",
                  background: formik.values.text
                    ? "linear-gradient(135deg, #564ab1, #3d3490)"
                    : "#e0e3ea",
                  color: formik.values.text ? "#fff" : "#9299a8",
                  fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
                  cursor: formik.values.text ? "pointer" : "default",
                  transition: "background 0.15s ease, color 0.15s ease",
                  flexShrink: 0,
                }}
              >
                {t("Backend.Groups.Post.AddComment")}
              </Button>
            </div>
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default GroupPostViewModal;
